import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import packageJSON from '../../../../../package.json';
import AddressItem from '../../../../components/AddressItem';
import Input from '../../../../components/Input';
import { t } from '../../../../lang';
import HelperService from '../../../../services/HelperService';
import RequestService from '../../../../services/RequestService';
import StorageService from '../../../../services/StorageService';
import { sendSlice } from '../../../../store/slices';

import './BuyBillingAddress.scss';

const CREDIT_CURRENCY = packageJSON?.creditCurrency ?? 'Credits';

const BuyBillingAddress = () => {
	const [addresses, setAddresses] = useState([]);

	const dispatch = useDispatch();
	const navigate = useNavigate();

	const [searchParams] = useSearchParams();
	const currencyId = Number(searchParams.get('currencyId'));

	const availableCurrencies = useSelector(store => store.app.availableCurrencies);
	const amount = useSelector(store => store.send.amount);
	const tariffData = useSelector(store => store.app.tariffData);

	const selectedAsset = availableCurrencies.find(currency => currency.Id === currencyId);

	const quotes = useSelector(store => store.app.quotes);
	const assetRate = quotes.find(quote => quote.Id === selectedAsset?.Id)?.Rate || 0;
	const billingRate = quotes.find(quote => quote.Symbol === 'Credits')?.Rate || 0;

	const billingFee = tariffData.tariff || 0;

	const amountWithTariff = billingFee + +amount;
	const amountToPayUsd = amountWithTariff * billingRate;
	const amountToPay = assetRate ? amountToPayUsd / assetRate : null;

	const sortedAddresses = addresses?.sort((a, b) => {
		if (a?.AmountUsd < b?.AmountUsd) return 1;
		if (a?.AmountUsd > b?.AmountUsd) return -1;
		return 0;
	});

	const getAddresses = useCallback(async () => {
		try {
			const response = await RequestService.GetAddressesByCurrency(selectedAsset?.Id);

			if (response.Status === 'Success') {
				setAddresses(response.Addresses || []);
			}
		} catch (error) {
			console.log(error);
		}
	}, [dispatch]);

	useEffect(() => {
		getAddresses();
		StorageService.UpdateQuotes();
		StorageService.UpdateTariffList();
	}, []);

	const onSelectAddress = address => {
		if (address.Amount < amountToPay) {
			HelperService.showNotification({
				className: 'BuyBillingAddress-NotEnough',
				icon: 'EXCLAMATIONMARK_CIRCLE_FILL',
				title: t('Attention'),
				description: t('Not enough balance'),
				iconSize: 32,
			});
		} else {
			const resolveFunc = response => {
				if (response?.Status === 'Success') {
					navigate(-3);
					dispatch(sendSlice.actions.setAmount(''));
					HelperService.showNotification({ type: 'success', title: t('Successful') });
				} else {
					navigate(-1);
					RequestService.BaseThenFunc(response);
				}
			};

			HelperService.confirmPin({
				requestMethod: RequestService.AddBillingBalance,
				resolveFunc,
				requestData: {
					SourceAddress: address.Address,
					CryptoCurrency: selectedAsset.Symbol,
					Amount: amountToPay,
				},
			});
		}
	};

	return (
		<div className='BuyBillingAddress'>
			<div className='BuyBillingAddress__field-header'>{t('Amount_v2')}</div>
			<Input className='BuyBillingAddress__amount FormBlock' value={amount} postfix={CREDIT_CURRENCY} disabled={true} />
			<div className='BuyBillingAddress__field-header'>{t('Amount to pay')}</div>
			<Input
				className='BuyBillingAddress__amount-to-pay FormBlock'
				value={HelperService.FormatCrypto(amountToPay, HelperService.GetCurrencyPrecisionByName(selectedAsset?.Symbol))}
				postfix={selectedAsset?.Symbol}
				disabled={true}
			/>
			<div className='BuyBillingAddress__amount-to-pay-usd'>
				${HelperService.FormatFiatWithoutCurrency(amountToPayUsd)}
			</div>
			<div className='BuyBillingAddress__field-header'>{t('Select address to pay')}</div>
			<div className='BuyBillingAddress__addresses-box'>
				<div className='FormBlock'>
					{sortedAddresses.map((address, index) => (
						<AddressItem
							className={amountToPay > address.Amount ? 'BuyBillingAddress__block-address' : ''}
							key={index}
							address={address}
							lastItem={index === sortedAddresses.length - 1}
							onClick={onSelectAddress}
						/>
					))}
				</div>
			</div>
		</div>
	);
};

export default BuyBillingAddress;
